import React, { useEffect, useState } from "react";
import axios from "axios";
import API_CONFIG2 from "../../API_CONFIG/API_CONFIG2";
import {
    FaCalendarAlt,
    FaClock,
    FaUserGraduate,
    FaTag,
    FaMoneyBillWave,
    FaChevronRight,
    FaArrowRight,
    FaCircle,
    FaCheckCircle,
} from "react-icons/fa";

const FormationDetail = ({ formationId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const API = `${API_CONFIG2.apiUrl}formation/infoFormationSite.php?id=${formationId}`;

    useEffect(() => {
        setLoading(true);
        axios
            .get(API)
            .then((res) => {
                setData(res.data);
            })
            .catch((err) => console.error("Erreur lors de la récupération:", err))
            .finally(() => setLoading(false));
    }, [formationId, API]);

    if (loading) {
        return (
            <div className="detail-loading-wrapper">
                <style>{`
                    .detail-loading-wrapper {
                        min-height: 100vh;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        background: #020617;
                    }
                    .loader-pulse {
                        width: 64px;
                        height: 64px;
                        background: #3b82f6;
                        border-radius: 50%;
                        animation: pulse 1.5s infinite;
                    }
                    @keyframes pulse {
                        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.6); }
                        70% { transform: scale(1); box-shadow: 0 0 0 30px rgba(59, 130, 246, 0); }
                        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
                    }
                `}</style>
                <div className="loader-pulse"></div>
            </div>
        );
    }

    if (!data || !data.formation) return null;

    const { formation, sessions } = data;

    // Helper pour formater les dates
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("-");
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
    };

    return (
        <div className="formation-detail-root">
            <style>{`
                /* ---------- RESET & BASE ---------- */
                .formation-detail-root {
                    --accent: #3b82f6;
                    --accent-glow: rgba(59, 130, 246, 0.35);
                    --text-muted: #94a3b8;
                    --text-light: #e2e8f0;
                    --glass-bg: rgba(15, 23, 42, 0.75);
                    --glass-border: rgba(255, 255, 255, 0.08);
                    --card-bg: rgba(255, 255, 255, 0.03);
                    
                    background: #020617;
                    min-height: 100vh;
                    color: #fff;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    line-height: 1.6;
                    overflow-x: hidden;
                }

                .formation-detail-root * {
                    box-sizing: border-box;
                }

                /* ---------- HERO ---------- */
                .hero-section {
                    position: relative;
                    width: 100%;
                    height: 75vh;
                    min-height: 480px;
                    max-height: 720px;
                    overflow: hidden;
                    display: flex;
                    align-items: flex-end;
                    justify-content: center;
                }

                .hero-image {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    object-position: center;
                    z-index: 0;
                }

                .hero-overlay {
                    position: absolute;
                    inset: 0;
                    z-index: 1;
                    background: linear-gradient(
                        to bottom,
                        rgba(2, 6, 23, 0.15) 0%,
                        rgba(2, 6, 23, 0.55) 40%,
                        rgba(2, 6, 23, 0.88) 75%,
                        #020617 100%
                    );
                }

                .hero-content {
                    position: relative;
                    z-index: 2;
                    width: 100%;
                    max-width: 1200px;
                    padding: 0 40px 60px 40px;
                    margin: 0 auto;
                }

                .hero-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background: rgba(59, 130, 246, 0.18);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    color: #60a5fa;
                    padding: 8px 20px;
                    border-radius: 100px;
                    font-weight: 600;
                    font-size: 0.8rem;
                    text-transform: uppercase;
                    letter-spacing: 1.2px;
                    border: 1px solid rgba(59, 130, 246, 0.15);
                    margin-bottom: 16px;
                }

                .hero-title {
                    font-size: clamp(2.4rem, 6vw, 4.2rem);
                    font-weight: 800;
                    line-height: 1.1;
                    margin-bottom: 16px;
                    letter-spacing: -0.02em;
                    background: linear-gradient(to bottom, #ffffff, #cbd5e1);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    max-width: 800px;
                }

                .hero-meta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 24px 40px;
                    margin-top: 8px;
                }

                .hero-meta-item {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: var(--text-light);
                    font-size: 0.95rem;
                    font-weight: 500;
                }

                .hero-meta-item svg {
                    color: var(--accent);
                    font-size: 1.1rem;
                }

                .hero-meta-item .label {
                    color: var(--text-muted);
                    font-weight: 400;
                    margin-right: 4px;
                }

                .hero-meta-item .prix {
                    color: #34d399;
                    font-weight: 700;
                    font-size: 1.1rem;
                }

                /* ---------- CONTENT WRAPPER ---------- */
                .content-wrapper {
                    max-width: 1200px;
                    margin: -40px auto 0 auto;
                    padding: 0 40px 80px 40px;
                    position: relative;
                    z-index: 3;
                }

                .content-card {
                    background: var(--glass-bg);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    border: 1px solid var(--glass-border);
                    border-radius: 32px;
                    padding: 50px 56px;
                    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6);
                }

                /* ---------- DESCRIPTION ---------- */
                .description-section {
                    margin-bottom: 48px;
                }

                .description-section h2 {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #fff;
                    margin-bottom: 12px;
                    letter-spacing: -0.01em;
                }

                .description-section p {
                    color: var(--text-muted);
                    font-size: 1.05rem;
                    line-height: 1.8;
                    max-width: 800px;
                }

                /* ---------- INFO GRID ---------- */
                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 16px;
                    margin-bottom: 52px;
                }

                .info-card {
                    background: var(--card-bg);
                    border: 1px solid var(--glass-border);
                    border-radius: 20px;
                    padding: 18px 22px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    transition: all 0.3s ease;
                }

                .info-card:hover {
                    background: rgba(255, 255, 255, 0.06);
                    border-color: rgba(59, 130, 246, 0.3);
                    transform: translateY(-2px);
                }

                .info-icon {
                    width: 44px;
                    height: 44px;
                    flex-shrink: 0;
                    background: rgba(59, 130, 246, 0.12);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--accent);
                    font-size: 1.15rem;
                }

                .info-label {
                    display: block;
                    font-size: 0.7rem;
                    color: var(--text-muted);
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }

                .info-value {
                    font-weight: 600;
                    font-size: 1rem;
                    color: #fff;
                }

                .info-value.prix-value {
                    color: #34d399;
                    font-size: 1.1rem;
                }

                /* ---------- SESSIONS LIST ---------- */
                .sessions-section {
                    border-top: 1px solid var(--glass-border);
                    padding-top: 40px;
                }

                .sessions-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .sessions-header h2 {
                    font-size: 1.4rem;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .sessions-header h2 svg {
                    color: var(--accent);
                }

                .sessions-count {
                    background: rgba(59, 130, 246, 0.15);
                    color: var(--accent);
                    padding: 4px 16px;
                    border-radius: 100px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }

                .sessions-list {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 14px;
                }

                .session-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 18px 24px;
                    background: var(--card-bg);
                    border: 1px solid var(--glass-border);
                    border-radius: 16px;
                    transition: all 0.3s ease;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .session-item:hover {
                    background: rgba(255, 255, 255, 0.05);
                    border-color: rgba(59, 130, 246, 0.25);
                    transform: translateX(4px);
                }

                .session-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .session-icon {
                    color: var(--accent);
                    font-size: 1.2rem;
                    flex-shrink: 0;
                }

                .session-info {
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .session-mois {
                    font-weight: 700;
                    font-size: 1rem;
                    color: #fff;
                }

                .session-dates {
                    font-size: 0.9rem;
                    color: var(--text-muted);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .session-dates .sep {
                    color: #475569;
                }

                .session-right {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .session-status {
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    color: #34d399;
                }

                .session-status svg {
                    font-size: 0.7rem;
                }

                .session-status.soon {
                    color: #fbbf24;
                }

                .session-status.full {
                    color: #ef4444;
                }

                .session-link {
                    color: var(--text-muted);
                    transition: color 0.3s;
                    font-size: 0.9rem;
                }

                .session-link:hover {
                    color: var(--accent);
                }

                /* ---------- EMPTY STATE ---------- */
                .empty-sessions {
                    text-align: center;
                    padding: 40px 20px;
                    color: var(--text-muted);
                }

                .empty-sessions svg {
                    font-size: 2.4rem;
                    color: #475569;
                    margin-bottom: 12px;
                }

                /* ---------- RESPONSIVE ---------- */
                @media (max-width: 992px) {
                    .hero-content { padding: 0 24px 40px 24px; }
                    .content-wrapper { padding: 0 24px 60px 24px; }
                    .content-card { padding: 32px 28px; }
                    .hero-title { font-size: clamp(2rem, 5vw, 3rem); }
                    .info-grid { grid-template-columns: repeat(2, 1fr); }
                }

                @media (max-width: 640px) {
                    .hero-section { height: 60vh; min-height: 360px; }
                    .hero-content { padding: 0 16px 32px 16px; }
                    .hero-title { font-size: 1.8rem; }
                    .hero-meta { gap: 12px 20px; }
                    .hero-meta-item { font-size: 0.8rem; }
                    .content-wrapper { padding: 0 16px 40px 16px; margin-top: -20px; }
                    .content-card { padding: 24px 18px; border-radius: 24px; }
                    .info-grid { grid-template-columns: 1fr; }
                    .session-item { flex-direction: column; align-items: flex-start; padding: 16px 18px; }
                    .session-right { width: 100%; justify-content: space-between; }
                    .sessions-header { flex-direction: column; align-items: flex-start; }
                    .description-section p { font-size: 0.95rem; }
                }
            `}</style>

            {/* ===== HERO ===== */}
            <section className="hero-section">
                <img
                    src={`${API_CONFIG2.apiUrl}${formation.image}`}
                    alt={formation.titre}
                    className="hero-image"
                />
                <div className="hero-overlay"></div>

                <div className="hero-content">
                    <div className="hero-badge">
                        <FaTag /> {formation.categorie}
                    </div>

                    <h1 className="hero-title">{formation.titre}</h1>

                    <div className="hero-meta">
                        <span className="hero-meta-item">
                            <FaClock />
                            <span className="label">Durée</span>
                            {formation.duree}
                        </span>
                        <span className="hero-meta-item">
                            <FaUserGraduate />
                            <span className="label">Places</span>
                            {formation.participantsLimit} participants
                        </span>
                        <span className="hero-meta-item">
                            <FaMoneyBillWave />
                            <span className="label">Investissement</span>
                            <span className="prix">{formation.prix} $USD</span>
                        </span>
                        <span className="hero-meta-item">
                            <FaCalendarAlt />
                            <span className="label">Sessions</span>
                            {sessions.length} disponible{sessions.length > 1 ? "s" : ""}
                        </span>
                    </div>
                </div>
            </section>

            {/* ===== CONTENT ===== */}
            <div className="content-wrapper">
                <div className="content-card">
                    {/* Description */}
                    <div className="description-section">
                        <h2>À propos de cette formation</h2>
                        <p>{formation.description}</p>
                    </div>

                    {/* Info grid */}
                    <div className="info-grid">
                        <div className="info-card">
                            <div className="info-icon"><FaClock /></div>
                            <div>
                                <span className="info-label">Durée</span>
                                <span className="info-value">{formation.duree}</span>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon"><FaUserGraduate /></div>
                            <div>
                                <span className="info-label">Places disponibles</span>
                                <span className="info-value">{formation.participantsLimit} participants</span>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon"><FaMoneyBillWave /></div>
                            <div>
                                <span className="info-label">Investissement</span>
                                <span className="info-value prix-value">{formation.prix} $USD</span>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon"><FaCalendarAlt /></div>
                            <div>
                                <span className="info-label">Sessions</span>
                                <span className="info-value">{sessions.length} disponible{sessions.length > 1 ? "s" : ""}</span>
                            </div>
                        </div>
                    </div>

                    {/* Sessions List */}
                    <div className="sessions-section">
                        <div className="sessions-header">
                            <h2>
                                <FaCalendarAlt />
                                Sessions disponibles
                            </h2>
                            <span className="sessions-count">
                                {sessions.length} session{sessions.length > 1 ? "s" : ""}
                            </span>
                        </div>

                        {sessions.length > 0 ? (
                            <div className="sessions-list">
                                {sessions.map((item) => (
                                    <div key={item.id} className="session-item">
                                        <div className="session-left">
                                            <FaCircle className="session-icon" />
                                            <div className="session-info">
                                                <span className="session-mois">
                                                    Session de {item.mois || "—"}
                                                </span>
                                                <span className="session-dates">
                                                    <span>Du {formatDate(item.debut)}</span>
                                                    <span className="sep">•</span>
                                                    <span>au {formatDate(item.fin)}</span>
                                                </span>
                                            </div>
                                        </div>
                                        <div className="session-right">
                                            <span className="session-status">
                                                <FaCheckCircle /> Disponible
                                            </span>
                                            <FaArrowRight className="session-link" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="empty-sessions">
                                <FaCalendarAlt />
                                <p>Aucune session planifiée pour le moment.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FormationDetail;